import React, { useEffect, useRef } from 'react';

// export type SwipeDirection = 'left' | 'right' | 'top' | 'bottom';

interface SwipeCallbacks {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeTop?: () => void;
  onSwipeBottom?: () => void;
}

interface SwipeOptions extends SwipeCallbacks {
  threshold?: number;
}

const useSwipe = (elementRef?: React.RefObject<HTMLElement | null>, options: SwipeOptions = {}) => {
  const { threshold = 50, onSwipeLeft, onSwipeRight, onSwipeTop, onSwipeBottom } = options;
  const startPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!elementRef) {
      return;
    }
    const element = elementRef.current;
    if (!element) return;

    const handleStart = (x: number, y: number) => {
      startPos.current = { x, y };
    };

    const handleEnd = (x: number, y: number) => {
      const deltaX = x - startPos.current.x;
      const deltaY = y - startPos.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY && absDeltaX > threshold) {
        if (deltaX > 0) {
          onSwipeRight?.();
        } else {
          onSwipeLeft?.();
        }
      } else if (absDeltaY > threshold) {
        if (deltaY > 0) {
          onSwipeBottom?.();
        } else {
          onSwipeTop?.();
        }
      }
    };

    const onTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientX, e.touches[0].clientY);
    const onTouchEnd = (e: TouchEvent) => handleEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    const onMouseDown = (e: MouseEvent) => handleStart(e.clientX, e.clientY);
    const onMouseUp = (e: MouseEvent) => handleEnd(e.clientX, e.clientY);

    element.addEventListener('touchstart', onTouchStart);
    element.addEventListener('touchend', onTouchEnd);
    element.addEventListener('mousedown', onMouseDown);
    element.addEventListener('mouseup', onMouseUp);

    return () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('mousedown', onMouseDown);
      element.removeEventListener('mouseup', onMouseUp);
    };
  }, [elementRef, threshold, onSwipeLeft, onSwipeRight, onSwipeTop, onSwipeBottom]);
};

export default useSwipe;
